/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useEventConfiguration } from "./hooks";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import AddNotificationGroup from "../../../../components/Administrations/Groups/AddNotificationGroup";
import AssociationNavigation from "../../../../components/Shared/Association/AssociationNavigation";
import { groupListSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";

const EventsConfiguration: React.FC = () => {
  const {
    selectedGroup,
    configData,
    groupDeleteHandler,
    tabData,
    handleListItemClick
  } = useEventConfiguration();
  const MAX_NOTIFICATION_GROUP = 1;
  const groupList = useSelector(groupListSelector) || [];
  const updatedGroupList = useMemo(
    () =>
      groupList.map((item: any) =>
        item.text === "System Default"
          ? {
              ...item,
              deviceCount: configData?.length || 0,
            }
          : item
      ),
    [groupList, configData]
  );
  const [activeTab, setActiveTab] = useState("0");
  const selectedGroupFromList = updatedGroupList.find(
    (group: any) => group?.value === selectedGroup?.value || group?.text === selectedGroup?.text
  );
  const selectedGroupDeviceCount =
    Number((selectedGroupFromList as any)?.deviceCount ?? (selectedGroupFromList as any)?.count ?? 0) || 0;
  const eventCount = configData?.length || 0;


  useEffect(() => {
    const activeTabIndex = Number(activeTab);
    const isInvalidTab = Number.isNaN(activeTabIndex) || activeTabIndex < 0 || activeTabIndex >= tabData.length;
    if (isInvalidTab) {
      setActiveTab("0");
    }
  }, [activeTab, tabData.length]);

  return (
    <>
      <div className="flex flex-row flex-nowrap">
        <AssociationNavigation
          searchPlaceholder={"Search groups..."}
          navHeaderName={"Notification groups"}
          selectedItemIndex={selectedGroup}
          isManagePermission={true}
          navItems={updatedGroupList}
          showCountForNone={true}
          ShowSearchFilterOnRecordsCount={MAX_NOTIFICATION_GROUP}
          addNavItem={AddNotificationGroup}
          confirmDeleteDialogOpener={DIALOGOPENFOR.DELETEGROUP}
          deleteConfirmationMessage={"Are you sure you want to delete notification group "}
          addDialogOpener={DIALOGOPENFOR.ADDGROUP}
          handleNavItemClick={handleListItemClick}
          deleteNavItemHandler={groupDeleteHandler}
          maxHeight={"calc(100vh - 15rem)"}
        />
        <div className="flex-1 min-h-[285px] pt-0 ml-3 max-w-[calc(100%-240px)]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="pl-4 pr-1 mt-1">
              <div style={{ borderBottom: "1px solid var(--color-border)" }}>
                <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                  {tabData.map((tab, index) => (
                    <TabsTrigger
                      key={index}
                      value={String(index)}
                      className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      <span className="inline-flex items-center gap-2">
                        <span>{tab.label}</span>
                        {tab.label === "Devices" && (
                          <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
                            {selectedGroupDeviceCount}
                          </span>
                        )}
                        {tab.label === "Events" && (
                          <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
                            {eventCount}
                          </span>
                        )}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </div>
            {tabData.map((tab, index) => (
              <TabsContent key={index} value={String(index)}>
                {tab.component}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default EventsConfiguration;
